(() => {
  const SELECTORS = {
    header: '[data-paths-header]',

    search: '[data-paths-search]',
    searchOpen: '[data-paths-search-open]',
    searchClose: '[data-paths-search-close]',
    searchInput: '[data-paths-search-input]',

    mobileMenu: '[data-paths-mobile-menu]',
    menuOpen: '[data-paths-menu-open]',
    menuClose: '[data-paths-menu-close]',

    desktopDropdown: '[data-paths-dropdown]',
    desktopDropdownToggle: '[data-paths-dropdown-toggle]',
    desktopDropdownPanel: '[data-paths-dropdown-panel]',

    mobileSubmenu: '[data-paths-mobile-submenu]',
    mobileSubmenuToggle: '[data-paths-mobile-submenu-toggle]',
    mobileSubmenuPanel: '[data-paths-mobile-submenu-panel]',
  };

  function initPathsHeader(header) {
    if (!header || header.dataset.pathsHeaderInitialized === 'true') return;

    header.dataset.pathsHeaderInitialized = 'true';

    const search = header.querySelector(SELECTORS.search);
    const searchOpenButtons = header.querySelectorAll(SELECTORS.searchOpen);
    const searchCloseButtons = header.querySelectorAll(SELECTORS.searchClose);
    const searchInput = header.querySelector(SELECTORS.searchInput);

    const mobileMenu = header.querySelector(SELECTORS.mobileMenu);
    const menuOpenButton = header.querySelector(SELECTORS.menuOpen);
    const menuCloseButtons = header.querySelectorAll(SELECTORS.menuClose);

    const desktopDropdowns = Array.from(
      header.querySelectorAll(SELECTORS.desktopDropdown)
    );

    const mobileSubmenus = Array.from(
      header.querySelectorAll(SELECTORS.mobileSubmenu)
    );

    let lastFocusedElement = null;

    /*
     * ======================================================
     * BODY LOCK
     * ======================================================
     */

    function syncBodyLock() {
      const overlayOpen =
        search?.classList.contains('is-open') ||
        mobileMenu?.classList.contains('is-open');

      document.body.classList.toggle(
        'paths-overlay-open',
        Boolean(overlayOpen)
      );
    }

    /*
     * ======================================================
     * DESKTOP DROPDOWNS
     * ======================================================
     */

    function closeDesktopDropdown(dropdown) {
      if (!dropdown) return;

      const toggle = dropdown.querySelector(
        SELECTORS.desktopDropdownToggle
      );

      const panel = dropdown.querySelector(
        SELECTORS.desktopDropdownPanel
      );

      dropdown.classList.remove('is-open');

      toggle?.setAttribute('aria-expanded', 'false');
      panel?.setAttribute('aria-hidden', 'true');
    }

    function closeAllDesktopDropdowns(exceptDropdown = null) {
      desktopDropdowns.forEach((dropdown) => {
        if (dropdown !== exceptDropdown) {
          closeDesktopDropdown(dropdown);
        }
      });
    }

    function openDesktopDropdown(dropdown) {
      if (!dropdown) return;

      const toggle = dropdown.querySelector(
        SELECTORS.desktopDropdownToggle
      );

      const panel = dropdown.querySelector(
        SELECTORS.desktopDropdownPanel
      );

      closeAllDesktopDropdowns(dropdown);

      dropdown.classList.add('is-open');

      toggle?.setAttribute('aria-expanded', 'true');
      panel?.setAttribute('aria-hidden', 'false');
    }

    function toggleDesktopDropdown(dropdown) {
      if (!dropdown) return;

      const isOpen = dropdown.classList.contains('is-open');

      if (isOpen) {
        closeDesktopDropdown(dropdown);
      } else {
        openDesktopDropdown(dropdown);
      }
    }

    desktopDropdowns.forEach((dropdown) => {
      const toggle = dropdown.querySelector(
        SELECTORS.desktopDropdownToggle
      );

      let closeTimer = null;

      toggle?.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();

        toggleDesktopDropdown(dropdown);
      });

      dropdown.addEventListener('mouseenter', () => {
        window.clearTimeout(closeTimer);
        openDesktopDropdown(dropdown);
      });

      dropdown.addEventListener('mouseleave', () => {
        closeTimer = window.setTimeout(() => {
          closeDesktopDropdown(dropdown);
        }, 140);
      });

      dropdown.addEventListener('focusin', () => {
        window.clearTimeout(closeTimer);
        openDesktopDropdown(dropdown);
      });

      dropdown.addEventListener('focusout', (event) => {
        const nextFocusedElement = event.relatedTarget;

        if (
          nextFocusedElement instanceof Node &&
          dropdown.contains(nextFocusedElement)
        ) {
          return;
        }

        closeTimer = window.setTimeout(() => {
          closeDesktopDropdown(dropdown);
        }, 80);
      });
    });

    /*
     * ======================================================
     * SEARCH
     * ======================================================
     */

    function closeSearch({ restoreFocus = true } = {}) {
      if (!search) return;

      search.classList.remove('is-open');
      search.setAttribute('aria-hidden', 'true');

      searchOpenButtons.forEach((button) => {
        button.setAttribute('aria-expanded', 'false');
      });

      syncBodyLock();

      if (
        restoreFocus &&
        lastFocusedElement instanceof HTMLElement
      ) {
        lastFocusedElement.focus();
      }
    }

    function openSearch(trigger) {
      if (!search) return;

      closeMenu({ restoreFocus: false });
      closeAllDesktopDropdowns();

      lastFocusedElement = trigger || document.activeElement;

      search.classList.add('is-open');
      search.setAttribute('aria-hidden', 'false');

      searchOpenButtons.forEach((button) => {
        button.setAttribute('aria-expanded', 'true');
      });

      syncBodyLock();

      window.requestAnimationFrame(() => {
        searchInput?.focus();
      });
    }

    /*
     * ======================================================
     * MOBILE MENU
     * ======================================================
     */

    function closeMenu({ restoreFocus = true } = {}) {
      if (!mobileMenu) return;

      mobileMenu.classList.remove('is-open');
      mobileMenu.setAttribute('aria-hidden', 'true');
      menuOpenButton?.setAttribute('aria-expanded', 'false');

      closeAllMobileSubmenus();

      syncBodyLock();

      if (
        restoreFocus &&
        lastFocusedElement instanceof HTMLElement
      ) {
        lastFocusedElement.focus();
      }
    }

    function openMenu(trigger) {
      if (!mobileMenu) return;

      closeSearch({ restoreFocus: false });
      closeAllDesktopDropdowns();

      lastFocusedElement = trigger || document.activeElement;

      mobileMenu.classList.add('is-open');
      mobileMenu.setAttribute('aria-hidden', 'false');
      menuOpenButton?.setAttribute('aria-expanded', 'true');

      syncBodyLock();

      window.requestAnimationFrame(() => {
        mobileMenu
          .querySelector('[data-paths-menu-close]')
          ?.focus();
      });
    }

    /*
     * ======================================================
     * MOBILE SUBMENUS
     * ======================================================
     */

    function closeMobileSubmenu(submenu) {
      if (!submenu) return;

      const toggle = submenu.querySelector(
        SELECTORS.mobileSubmenuToggle
      );

      const panel = submenu.querySelector(
        SELECTORS.mobileSubmenuPanel
      );

      submenu.classList.remove('is-open');

      toggle?.setAttribute('aria-expanded', 'false');
      panel?.setAttribute('aria-hidden', 'true');

      if (panel) {
        panel.style.maxHeight = '';
      }
    }

    function closeAllMobileSubmenus(exceptSubmenu = null) {
      mobileSubmenus.forEach((submenu) => {
        if (submenu !== exceptSubmenu) {
          closeMobileSubmenu(submenu);
        }
      });
    }

    function openMobileSubmenu(submenu) {
      if (!submenu) return;

      const toggle = submenu.querySelector(
        SELECTORS.mobileSubmenuToggle
      );

      const panel = submenu.querySelector(
        SELECTORS.mobileSubmenuPanel
      );

      closeAllMobileSubmenus(submenu);

      submenu.classList.add('is-open');

      toggle?.setAttribute('aria-expanded', 'true');
      panel?.setAttribute('aria-hidden', 'false');

      if (panel) {
        panel.style.maxHeight = `${panel.scrollHeight}px`;
      }
    }

    function toggleMobileSubmenu(submenu) {
      if (!submenu) return;

      const isOpen = submenu.classList.contains('is-open');

      if (isOpen) {
        closeMobileSubmenu(submenu);
      } else {
        openMobileSubmenu(submenu);
      }
    }

    mobileSubmenus.forEach((submenu) => {
      const toggle = submenu.querySelector(
        SELECTORS.mobileSubmenuToggle
      );

      toggle?.addEventListener('click', (event) => {
        event.preventDefault();

        toggleMobileSubmenu(submenu);
      });
    });

    /*
     * ======================================================
     * SEARCH AND MENU EVENTS
     * ======================================================
     */

    searchOpenButtons.forEach((button) => {
      button.addEventListener('click', () => {
        openSearch(button);
      });
    });

    searchCloseButtons.forEach((button) => {
      button.addEventListener('click', () => {
        closeSearch();
      });
    });

    menuOpenButton?.addEventListener('click', () => {
      openMenu(menuOpenButton);
    });

    menuCloseButtons.forEach((button) => {
      button.addEventListener('click', () => {
        closeMenu();
      });
    });

    /*
     * ======================================================
     * OUTSIDE CLICK
     * ======================================================
     */

    document.addEventListener('click', (event) => {
      if (!(event.target instanceof Node)) return;

      const clickedInsideDropdown = desktopDropdowns.some(
        (dropdown) => dropdown.contains(event.target)
      );

      if (!clickedInsideDropdown) {
        closeAllDesktopDropdowns();
      }
    });

    /*
     * ======================================================
     * KEYBOARD
     * ======================================================
     */

    document.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;

      const openDropdown = desktopDropdowns.find(
        (dropdown) => dropdown.classList.contains('is-open')
      );

      if (openDropdown) {
        const toggle = openDropdown.querySelector(
          SELECTORS.desktopDropdownToggle
        );

        closeDesktopDropdown(openDropdown);
        toggle?.focus();
        return;
      }

      const openMobileSubmenu = mobileSubmenus.find(
        (submenu) => submenu.classList.contains('is-open')
      );

      if (openMobileSubmenu) {
        const toggle = openMobileSubmenu.querySelector(
          SELECTORS.mobileSubmenuToggle
        );

        closeMobileSubmenu(openMobileSubmenu);
        toggle?.focus();
        return;
      }

      if (search?.classList.contains('is-open')) {
        closeSearch();
        return;
      }

      if (mobileMenu?.classList.contains('is-open')) {
        closeMenu();
      }
    });

    /*
     * ======================================================
     * RESIZE
     * ======================================================
     */

    window.addEventListener(
      'resize',
      () => {
        if (window.innerWidth > 989) {
          if (mobileMenu?.classList.contains('is-open')) {
            closeMenu({ restoreFocus: false });
          }
        } else {
          closeAllDesktopDropdowns();
        }

        mobileSubmenus.forEach((submenu) => {
          if (!submenu.classList.contains('is-open')) return;

          const panel = submenu.querySelector(
            SELECTORS.mobileSubmenuPanel
          );

          if (panel) {
            panel.style.maxHeight = `${panel.scrollHeight}px`;
          }
        });
      },
      { passive: true }
    );
  }

  function initAllPathsHeaders(root = document) {
    root
      .querySelectorAll(SELECTORS.header)
      .forEach(initPathsHeader);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      initAllPathsHeaders();
    });
  } else {
    initAllPathsHeaders();
  }

  document.addEventListener('shopify:section:load', (event) => {
    initAllPathsHeaders(event.target);
  });
})();